import type { Json } from "@/integrations/supabase/types";
import { loadSupabase } from "@/lib/loadSupabase";
import { startWebVitals } from "@/lib/webVitals";
import {
  clearStaleAppCaches,
  isChunkLoadError,
  isChunkRecoveryInProgress,
  reserveChunkReload,
} from "@/lib/chunkRecovery";

export type AnalyticsParams = Record<string, string | number | boolean | undefined>;

declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: (...args: unknown[]) => void;
  }
}

interface QueuedEvent {
  name: string;
  path: string;
  properties: AnalyticsParams;
  queuedAt: number;
}

interface StoredSession {
  id: string;
  lastSeen: number;
}

const measurementId = import.meta.env.VITE_GA_MEASUREMENT_ID?.trim();
const sessionStorageKey = "geominer_analytics_session_v1";
const deliveryStorageKey = "geominer_analytics_delivery_v1";
const qaSessionStorageKey = "geominer_internal_qa_session_v1";
const qaQueryParameters = ["qa", "audit", "release", "admin-client-test"];
const sessionTimeoutMs = 30 * 60 * 1000;
const flushDelayMs = 1200;
const failedFlushRetryMs = 15_000;
const maxQueuedEvents = 100;
const queuedEventMaxAgeMs = 30 * 60 * 1000;
const pageViewDedupWindowMs = 1_000;
const queue: QueuedEvent[] = [];
let initialized = false;
let flushing = false;
let flushTimer: number | undefined;
let analyticsSuppressed = !isProductionAnalyticsHost() || isQualityAssuranceSession() || isLikelyAutomatedClient();

// Keep the browser-to-first-party and browser-to-GA boundaries aligned with
// the database contracts. Values receive stricter validation in PostgreSQL;
// this map prevents accidental free text or new ad-hoc keys leaving the page.
const eventPropertyKeys: Record<string, readonly string[]> = {
  page_view: [],
  sign_up: ["method", "status"],
  sign_in: ["method"],
  home_cta: ["cta", "placement"],
  discovery_cta: ["source", "cta", "placement"],
  home_guide_open: ["path", "guide_type"],
  home_specimen_start: ["clue", "destination"],
  field_chart_action: ["action"],
  mohs_guide_action: ["action"],
  streak_guide_action: ["action", "colour"],
  cleavage_guide_action: ["action", "cleavage"],
  lustre_guide_action: ["action", "lustre"],
  colour_guide_action: ["action", "colour"],
  magnetism_guide_action: ["action", "magnetism"],
  transparency_guide_action: ["action", "transparency"],
  habit_guide_action: ["action", "habit"],
  crystal_guide_action: ["action", "lustre", "hardness"],
  geode_guide_action: ["action", "setting", "interior"],
  meteorite_guide_action: ["action", "indicator", "value", "outcome"],
  fluorescence_guide_action: ["action", "uv_band", "response"],
  rockhounding_guide_action: ["action", "site_type", "activity"],
  ontario_guide_action: ["action", "setting", "clue"],
  rock_tumbling_guide_action: ["action", "hardness", "structure"],
  specimen_label_tool_action: ["action", "template", "copies"],
  specimen_cleaning_guide_action: ["action", "condition", "method"],
  rock_value_guide_action: ["action", "category", "goal"],
  collection_inventory_action: ["action", "count_band"],
  geologic_time_action: ["action", "age_band"],
  strike_dip_action: ["action", "mode", "dip_band"],
  grain_size_action: ["action", "input_unit", "size_band"],
  soil_texture_action: ["action", "texture_group"],
  rqd_action: ["action", "unit", "recovery_band", "rqd_band"],
  rock_weight_action: ["action", "shape", "density_source", "mass_band"],
  slope_gradient_action: ["action", "input_mode", "direction", "slope_band"],
  coordinate_converter_action: ["action", "input_format", "output_format"],
  map_scale_action: ["action", "mode", "scale_band"],
  coordinate_distance_action: ["action", "distance_band", "bearing_status"],
  true_thickness_action: ["action", "method", "angle_band"],
  utm_converter_action: ["action", "mode", "zone_source"],
  ore_grade_action: ["action", "input_unit", "output_unit"],
  contained_metal_action: ["action", "tonnage_unit", "grade_unit"],
  weighted_interval_grade_action: ["action", "grade_unit", "interval_count", "gap_status"],
  drillhole_desurvey_action: ["action", "length_unit", "station_count", "trajectory_band"],
  igneous_guide_action: ["action", "texture", "composition"],
  sedimentary_guide_action: ["action", "clue", "support"],
  metamorphic_guide_action: ["action", "fabric", "composition"],
  fossil_guide_action: ["action", "evidence", "form"],
  rock_id_quiz_action: ["action", "score_band", "family"],
  mineral_id_quiz_action: ["action", "score_band", "topic"],
  fossil_id_quiz_action: ["action", "score_band", "topic"],
  worksheet_action: ["action"],
  rock_cycle_action: ["action"],
  specific_gravity_action: ["action", "method"],
  porosity_action: ["action", "method", "porosity_band"],
  darcy_flow_action: ["action", "head_unit", "conductivity_unit", "area_unit", "gradient_band"],
  aquifer_transmissivity_action: ["action", "solve_for", "conductivity_unit", "thickness_unit", "transmissivity_unit"],
  radiometric_dating_action: ["action", "solve_for", "observation_mode", "age_unit", "half_life_unit"],
  geothermal_gradient_action: ["action", "solve_for", "reference_temperature_unit", "target_temperature_unit", "reference_depth_unit", "target_depth_unit", "gradient_unit"],
  hydraulic_conductivity_lab_action: ["action", "method", "length_unit", "volume_unit", "time_unit"],
  lugeon_packer_test_action: ["action", "pressure_unit", "flow_unit", "length_unit", "duration_status"],
  break_even_grade_action: ["action", "cost_basis", "price_unit", "payability_status"],
  mine_dilution_action: ["action", "dilution_basis", "grade_unit", "tonnage_unit", "recovery_band"],
  rock_mass_rating_action: ["action", "application", "rmr_class", "strength_band", "groundwater_condition"],
  q_system_action: ["action", "quality_class", "jointing_band", "water_band"],
  rock_mass_classification_action: ["action", "destination"],
  point_load_strength_action: ["action", "test_type", "load_unit", "length_unit", "strength_band"],
  uniaxial_compressive_strength_action: ["action", "load_unit", "length_unit", "strength_band", "ratio_band"],
  atterberg_limits_action: ["action", "result_status", "chart_region", "liquid_limit_band", "natural_water_status", "consistency_band"],
  soil_compaction_action: ["action", "density_basis", "reference_status", "target_status", "compaction_band", "moisture_position", "unit_family"],
  sieve_analysis_action: ["action", "input_mode", "row_band", "d_values_status", "gradation_check", "standard_boundaries", "mass_unit_family"],
  uscs_classification_action: ["action", "result_status", "division", "symbol_family", "fines_band", "missing_inputs", "organic_review"],
  stereonet_action: ["action", "display_mode", "result_status", "dataset_band"],
  theis_drawdown_action: ["action", "unit_system", "time_unit", "u_band"],
  stripping_ratio_action: ["action", "basis", "input_mode", "scope", "ratio_band"],
  guide_hub_action: ["action", "category", "destination_type"],
  related_resource_open: ["source_type", "destination_type", "relationship"],
  daily_challenge_view: ["state"],
  daily_challenge_answer: ["group", "outcome"],
  daily_challenge_source_open: ["group"],
  daily_challenge_share: ["method"],
  daily_challenge_landing: [],
  daily_reminder_downloaded: [],
  daily_reminder_landing: [],
  daily_fun_fact_open: [],
  daily_fun_fact_share: ["method"],
  daily_fun_fact_landing: [],
  daily_mineral_open: [],
  daily_mineral_share: ["method"],
  daily_mineral_landing: [],
  identify_start: ["specimen_type", "image_count", "access"],
  identify_complete: ["specimen_type", "image_count", "top_confidence", "access"],
  identify_failed: ["specimen_type", "error_type"],
  identify_share: ["method", "source", "public_find"],
  public_find_share_landing: [],
  identify_auth_open: ["source", "mode"],
  identify_quick_id_open: ["source"],
  identification_guide_open: ["source"],
  identify_guest_access_probe: ["outcome"],
  identify_funnel_step: ["step", "specimen_type", "image_count", "access"],
  quick_id_filter: ["property", "value"],
  quick_id_preset: ["preset"],
  quick_id_share: ["method"],
  quick_id_share_landing: [],
  quick_id_result_open: ["rank"],
  quick_id_library_retry: [],
  reference_share: ["category"],
  reference_share_landing: [],
  reference_field_guide_open: [],
  reference_compare: ["category", "comparison_slug", "destination"],
  reference_source_open: ["category"],
  reference_saved: ["category"],
  reference_unsaved: ["category"],
  reference_saved_open: ["category", "placement"],
  career_quiz_start: [],
  career_quiz_complete: ["top_match", "saved_on_device"],
  career_quiz_share: ["career"],
  career_quiz_restore: ["top_match", "age_days"],
  career_quiz_result_open: ["career", "rank", "placement", "result_source"],
  career_profile_cta: ["career", "cta"],
  career_salary_action: ["action", "category"],
  mining_job_guide_action: ["action", "pathway", "work_style"],
  training_resume_clicked: ["track_id", "signed_in", "source", "placement"],
  classroom_cta: ["cta"],
  onboarding_view: [],
  onboarding_step_open: ["step", "destination"],
  onboarding_dismissed: ["completed_steps"],
  community_post_created: ["attached_find"],
  community_post_shared: ["attached_find"],
  site_search: ["results", "placement"],
  search_result_open: ["source_type", "placement"],
  certificate_share: ["method"],
  finds_export: ["count", "scope"],
  guide_share: ["guide_type", "method"],
  guide_share_landing: ["guide_type"],
  public_content_share: [],
  public_content_share_landing: [],
  profile_share: [],
  profile_share_landing: [],
  partner_interest: ["partner_type"],
  partner_outbound: ["partner_type"],
  contact_interest: ["role", "has_organization"],
  pwa_install_available: ["platform"],
  pwa_banner_shown: ["platform", "native_prompt"],
  pwa_banner_dismissed: ["platform"],
  pwa_install_prompt_result: ["outcome", "platform"],
  pwa_install_completed: ["platform"],
  app_error: ["type", "error_category"],
  web_vital: ["name", "rating", "value", "measurement_version"],
};

function isProductionAnalyticsHost() {
  if (typeof window === "undefined") return false;
  const hostname = window.location.hostname.toLowerCase();
  return hostname === "geominer.ca" || hostname === "www.geominer.ca";
}

function isQualityAssuranceSession() {
  if (typeof window === "undefined") return false;

  try {
    if (window.sessionStorage.getItem(qaSessionStorageKey) === "1") return true;
    const search = new URLSearchParams(window.location.search);
    if (!qaQueryParameters.some((parameter) => search.has(parameter))) return false;
    window.sessionStorage.setItem(qaSessionStorageKey, "1");
    return true;
  } catch {
    return qaQueryParameters.some((parameter) => new URLSearchParams(window.location.search).has(parameter));
  }
}

export function isLikelyAutomatedClient(
  userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent,
  webdriver = typeof navigator === "undefined" ? false : navigator.webdriver,
) {
  if (webdriver) return true;
  return /(?:bot\b|crawler|spider|headless|lighthouse|pagespeed|google-inspectiontool|googleother|bingpreview|facebookexternalhit|slurp|baiduspider|semrush|ahrefs|mj12)/i.test(userAgent);
}

function discardQueuedEvents() {
  queue.length = 0;
  try {
    localStorage.removeItem(deliveryStorageKey);
  } catch {
    // Storage availability must never affect the product.
  }
}

function analyticsBlocked() {
  if (analyticsSuppressed) {
    discardQueuedEvents();
    return true;
  }
  if (isLikelyAutomatedClient()) {
    analyticsSuppressed = true;
    discardQueuedEvents();
    return true;
  }
  if (!isQualityAssuranceSession()) return false;
  analyticsSuppressed = true;
  discardQueuedEvents();
  return true;
}

function createSessionId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return "10000000-1000-4000-8000-100000000000".replace(/[018]/g, (character) =>
    (Number(character) ^ (Math.random() * 16 >> Number(character) / 4)).toString(16),
  );
}

function getSessionId() {
  const now = Date.now();

  try {
    const stored = JSON.parse(localStorage.getItem(sessionStorageKey) || "null") as StoredSession | null;
    const current = stored && now - stored.lastSeen < sessionTimeoutMs
      ? { ...stored, lastSeen: now }
      : { id: createSessionId(), lastSeen: now };
    localStorage.setItem(sessionStorageKey, JSON.stringify(current));
    return current.id;
  } catch {
    return createSessionId();
  }
}

function referrerHost() {
  if (!document.referrer) return undefined;
  try {
    const host = new URL(document.referrer).hostname.toLowerCase();
    return host === window.location.hostname.toLowerCase() ? undefined : host;
  } catch {
    return undefined;
  }
}

function sanitizeProperties(params: AnalyticsParams): AnalyticsParams {
  return Object.fromEntries(
    Object.entries(params)
      .filter(([, value]) => ["string", "number", "boolean"].includes(typeof value))
      .slice(0, 20)
      .map(([key, value]) => [key.slice(0, 64), typeof value === "string" ? value.slice(0, 200) : value]),
  );
}

function sanitizeEventProperties(name: string, params: AnalyticsParams) {
  if (name !== "app_error") {
    const allowedKeys = new Set(eventPropertyKeys[name] ?? []);
    return sanitizeProperties(Object.fromEntries(
      Object.entries(params).filter(([key]) => allowedKeys.has(key)),
    ));
  }

  const type = typeof params.type === "string" && /^[a-z0-9_-]{1,40}$/i.test(params.type)
    ? params.type
    : "unknown";
  const suppliedCategory = typeof params.error_category === "string" ? params.error_category : "";
  const allowedCategories = new Set(["chunk_load", "network", "authentication", "browser_storage", "unknown"]);
  const errorCategory = allowedCategories.has(suppliedCategory)
    ? suppliedCategory
    : classifyClientError(params.message);
  return { type, error_category: errorCategory };
}

function currentPath() {
  return sanitizeAnalyticsPath(window.location.pathname);
}

export function sanitizeAnalyticsPath(value: string) {
  let path = String(value || "/").split(/[?#]/, 1)[0] || "/";
  if (!path.startsWith("/") || path.length > 300) return "/unknown";

  if (/^\/my-finds\/[^/]+/i.test(path)) path = path.replace(/^\/my-finds\/[^/]+/i, "/my-finds/:id");
  if (/^\/profile\/[^/]+/i.test(path)) path = path.replace(/^\/profile\/[^/]+/i, "/profile/:id");
  if (/^\/verify\/[^/]+/i.test(path)) path = path.replace(/^\/verify\/[^/]+/i, "/verify/:certificate");
  if (/^\/classroom\/(?!create(?:\/|$))[^/]+/i.test(path)) {
    path = path.replace(/^\/classroom\/[^/]+/i, "/classroom/:id");
  }

  return path.replace(/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}/gi, ":id");
}

function eventPayload(event: QueuedEvent) {
  return { name: event.name, path: event.path, properties: event.properties };
}

function persistQueuedEvents() {
  try {
    if (queue.length === 0) {
      localStorage.removeItem(deliveryStorageKey);
      return;
    }
    localStorage.setItem(deliveryStorageKey, JSON.stringify(queue.slice(-maxQueuedEvents)));
  } catch {
    // In-memory delivery still works when storage is blocked or full.
  }
}

function restoreQueuedEvents(now = Date.now()) {
  try {
    const parsed = JSON.parse(localStorage.getItem(deliveryStorageKey) || "[]") as unknown;
    if (!Array.isArray(parsed)) {
      localStorage.removeItem(deliveryStorageKey);
      return;
    }

    const restored = parsed.slice(-maxQueuedEvents).flatMap((value): QueuedEvent[] => {
      if (!value || typeof value !== "object") return [];
      const event = value as Record<string, unknown>;
      if (
        typeof event.name !== "string" ||
        !Object.prototype.hasOwnProperty.call(eventPropertyKeys, event.name)
      ) {
        return [];
      }
      if (typeof event.path !== "string" || typeof event.queuedAt !== "number") return [];
      if (!Number.isFinite(event.queuedAt) || event.queuedAt > now || now - event.queuedAt > queuedEventMaxAgeMs) return [];
      const properties = event.properties && typeof event.properties === "object" && !Array.isArray(event.properties)
        ? event.properties as AnalyticsParams
        : {};
      return [{
        name: event.name,
        path: sanitizeAnalyticsPath(event.path),
        properties: sanitizeEventProperties(event.name, properties),
        queuedAt: event.queuedAt,
      }];
    });

    queue.push(...restored);
    if (queue.length > maxQueuedEvents) queue.splice(0, queue.length - maxQueuedEvents);
    persistQueuedEvents();
  } catch {
    discardQueuedEvents();
  }
}

function scheduleFlush(delayMs = flushDelayMs) {
  if (flushTimer !== undefined) return;
  if (typeof navigator !== "undefined" && !navigator.onLine) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = undefined;
    void flushFirstPartyEvents();
  }, delayMs);
}

async function flushFirstPartyEvents() {
  if (analyticsBlocked() || flushing || queue.length === 0) return;
  if (typeof navigator !== "undefined" && !navigator.onLine) {
    persistQueuedEvents();
    return;
  }
  if (flushTimer !== undefined) {
    window.clearTimeout(flushTimer);
    flushTimer = undefined;
  }
  flushing = true;
  const events = queue.splice(0, 20);
  let failedEvents = events;

  try {
    const supabase = await loadSupabase();
    const sessionId = getSessionId();
    const webVitals = events.filter((event) => event.name === "web_vital");
    const productEvents = events.filter((event) => event.name !== "web_vital");
    const deliveries: Array<{
      events: QueuedEvent[];
      request: PromiseLike<{ error: { message?: string } | null }>;
    }> = [];

    if (productEvents.length > 0) {
      deliveries.push({
        events: productEvents,
        request: supabase.rpc("record_analytics_events", {
          p_events: productEvents.map(eventPayload) as unknown as Json,
          p_session_id: sessionId,
          p_referrer_host: referrerHost(),
        }),
      });
    }
    if (webVitals.length > 0) {
      deliveries.push({
        events: webVitals,
        request: supabase.rpc("record_web_vital_events", {
          p_events: webVitals.map(eventPayload) as unknown as Json,
          p_session_id: sessionId,
        }),
      });
    }

    failedEvents = (await Promise.all(deliveries.map(async (delivery) => {
      try {
        const result = await delivery.request;
        if (!result.error) return [];
        if (import.meta.env.DEV) console.info("First-party analytics unavailable", result.error.message);
        return delivery.events;
      } catch {
        return delivery.events;
      }
    }))).flat();
  } catch {
    // Analytics must never interrupt the product experience.
  } finally {
    if (!analyticsSuppressed && failedEvents.length > 0) {
      queue.unshift(...failedEvents);
      if (queue.length > maxQueuedEvents) queue.length = maxQueuedEvents;
    }
    if (analyticsSuppressed) discardQueuedEvents();
    else persistQueuedEvents();
    flushing = false;
    if (queue.length > 0) scheduleFlush(failedEvents.length > 0 ? failedFlushRetryMs : flushDelayMs);
  }
}

function queueFirstPartyEvent(name: string, params: AnalyticsParams = {}, path = currentPath()) {
  if (analyticsBlocked()) return;
  getSessionId();
  queue.push({
    name,
    path: sanitizeAnalyticsPath(path),
    properties: sanitizeEventProperties(name, params),
    queuedAt: Date.now(),
  });
  if (queue.length > maxQueuedEvents) queue.splice(0, queue.length - maxQueuedEvents);
  persistQueuedEvents();
  if (queue.length >= 10) void flushFirstPartyEvents();
  else scheduleFlush();
}

function getClientErrorMessage(value: unknown) {
  if (value instanceof Error) return value.message;
  if (typeof value === "string") return value;
  if (value && typeof value === "object" && "message" in value) {
    const message = (value as { message?: unknown }).message;
    if (typeof message === "string") return message;
  }
  return String(value || "");
}

function hasRecentQueuedPageView(path: string, now = Date.now()) {
  for (let index = queue.length - 1; index >= 0; index -= 1) {
    const event = queue[index];
    if (event.name !== "page_view") continue;
    return event.path === path
      && now >= event.queuedAt
      && now - event.queuedAt <= pageViewDedupWindowMs;
  }
  return false;
}

const boundedClientErrorName = /^(?:Abort|Network|NotAllowed|QuotaExceeded|Reference|Security|Timeout|Type)Error$/;

function getClientErrorName(value: unknown) {
  if (value instanceof Error) return value.name;
  if (value && typeof value === "object" && "name" in value) {
    const name = (value as { name?: unknown }).name;
    if (typeof name === "string") return name;
  }
  return "";
}

export function classifyClientErrorType(source: string, value: unknown) {
  if (isChunkLoadError(value)) return "chunk_load";
  const name = getClientErrorName(value);
  if (!boundedClientErrorName.test(name)) return source;
  const suffix = name.replace(/([a-z0-9])([A-Z])/g, "$1_$2").toLowerCase();
  return `${source}_${suffix}`;
}

export function classifyClientError(value: unknown) {
  const message = getClientErrorMessage(value);
  const name = getClientErrorName(value);
  if (isChunkLoadError(value)) return "chunk_load";
  if (name === "NetworkError" || name === "TimeoutError") return "network";
  if (name === "QuotaExceededError") return "browser_storage";
  if (/network|failed to fetch|load failed|timed? ?out|offline|connection/i.test(message)) return "network";
  if (/jwt|auth|sign.?in|unauthori[sz]ed|session/i.test(message)) return "authentication";
  if (/quota|storage|indexeddb|localstorage|sessionstorage/i.test(message)) return "browser_storage";
  return "unknown";
}

function recoverUnhandledChunk(error: unknown) {
  if (!isChunkLoadError(error) || isChunkRecoveryInProgress() || !reserveChunkReload(error)) return;
  void clearStaleAppCaches().finally(() => window.location.reload());
}

export function initAnalytics() {
  if (analyticsBlocked() || initialized) return;
  if (!isProductionAnalyticsHost()) {
    analyticsSuppressed = true;
    discardQueuedEvents();
    return;
  }
  initialized = true;
  if (queue.length === 0) restoreQueuedEvents();
  if (queue.length > 0) scheduleFlush();

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushFirstPartyEvents();
  });

  window.addEventListener("online", () => {
    void flushFirstPartyEvents();
  });

  window.addEventListener("error", (event) => {
    const error = event.error ?? event.message;
    queueFirstPartyEvent("app_error", {
      type: classifyClientErrorType("window_error", error),
      error_category: classifyClientError(error),
    });
    recoverUnhandledChunk(error);
  });

  window.addEventListener("unhandledrejection", (event) => {
    queueFirstPartyEvent("app_error", {
      type: classifyClientErrorType("unhandled_rejection", event.reason),
      error_category: classifyClientError(event.reason),
    });
    recoverUnhandledChunk(event.reason);
  });

  // A Web Vital describes the document's initial navigation, even when it is
  // reported after the visitor has moved to another client-side route.
  const initialLoadPath = currentPath();
  startWebVitals((metrics) => {
    for (const metric of metrics) {
      queueFirstPartyEvent("web_vital", { ...metric }, initialLoadPath);
    }
    void flushFirstPartyEvents();
  });

  if (!measurementId || !/^G-[A-Z0-9]+$/i.test(measurementId)) return;

  window.dataLayer = window.dataLayer || [];
  window.gtag = (...args: unknown[]) => window.dataLayer?.push(args);
  window.gtag("js", new Date());
  window.gtag("config", measurementId, {
    send_page_view: false,
    anonymize_ip: true,
  });

  const script = document.createElement("script");
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(measurementId)}`;
  document.head.appendChild(script);
}

/**
 * Keep internal administration and QA activity out of acquisition metrics.
 * The database independently rejects admin events as a second line of defence.
 */
export function setAnalyticsSuppressed(suppressed: boolean) {
  analyticsSuppressed = suppressed;
  if (!suppressed) return;

  discardQueuedEvents();
  if (flushTimer !== undefined) {
    window.clearTimeout(flushTimer);
    flushTimer = undefined;
  }
}

export function trackPageView(path: string) {
  if (analyticsBlocked()) return;
  const routePath = sanitizeAnalyticsPath(path);
  // Route effects can briefly re-run while auth/admin state settles. Keep those
  // same-tick emissions from inflating sponsor-facing pageview totals, while a
  // real route change (including navigating away and back) still counts.
  if (hasRecentQueuedPageView(routePath)) return;
  queueFirstPartyEvent("page_view", {}, routePath);

  if (!window.gtag || !measurementId) return;
  window.gtag("event", "page_view", {
    page_path: routePath,
    page_location: `${window.location.origin}${routePath}`,
  });
}

export function trackEvent(name: string, params: AnalyticsParams = {}) {
  if (analyticsBlocked()) return;
  const safeParams = sanitizeEventProperties(name, params);
  queueFirstPartyEvent(name, safeParams);
  window.gtag?.("event", name, safeParams);
}

export function flushAnalytics() {
  return flushFirstPartyEvents();
}
